import { type ITrackingProps, TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS } from '../constants';

/**
 * Single source of truth for every betslip tracking payload. Extracted so the shared betslip
 * (`@cx-web/feature-betslip`, used by atlas/atlas-astro/product-plus) and the OC CDN betslip paths
 * (`UKBetslip.tsx`, product-plus's `Header.tsx` OC wiring) build byte-identical events for the same
 * user action instead of duplicating `trackingActions.eventTracking({...})` calls inline. See NGF-6313
 * (the BET_SLIP_VIEW event was the first, and until now only, one extracted this way) and the
 * bet-tracking unification ticket that added the rest.
 */

// Minimal structural shape for BET_SLIP_VIEW — deliberately not imported from
// `@cx-web/feature-betslip`'s `Bet`/`TrackBet` types to keep this package dependency-free (it would
// otherwise be a circular workspace dependency, since feature-betslip depends on feature-tracking).
// Any `TrackBet[]` the betslip package already has is structurally assignable here.
export type BetSlipViewBet = {
  bestOddsBookmakers?: string;
  bestOddsDecimal?: number;
  bestOddsFractional?: string;
  bestOddsUs?: number;
  marketName?: string;
  name?: string;
  subeventName?: string;
};

export const buildBetSlipViewEvent = (betSlip: BetSlipViewBet[], stake?: number): ITrackingProps => {
  const isAcca = betSlip.length > 1;

  return {
    event: TRACKING_EVENTS.BET_SLIP_VIEW,
    eventCategory: TRACKING_CATEGORIES.BETTING,
    eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_VIEW],
    eventLabel: isAcca ? 'multi_bets' : 'single_bet',
    accumulator: isAcca ? 'yes' : 'no',
    marketName: betSlip.map(b => b.marketName).join(','),
    subeventName: betSlip.map(b => b.subeventName).join(','),
    bookieCode: betSlip.map(b => b.bestOddsBookmakers).join(','),
    betName: betSlip.map(b => b.name).join(','),
    // Prefer american → fractional → decimal to match getSelectedBookieBetOdds() and support all 4
    // odds formats (american, decimal, fractional, traditional). Traditional also uses fractional
    // representation. This ensures BET_SLIP_VIEW and BET_PLACED report the same bet in the same
    // format for UK fractional-odds users.
    betOdds: betSlip.map(b => b.bestOddsUs ?? b.bestOddsFractional ?? b.bestOddsDecimal).join(','),
    betStake: stake?.toString(),
  };
};

export type AddOrRemoveBetslipEventInput = {
  betLocation?: string;
  betName?: string;
  betOdds?: string;
  bookieCode?: string;
  eventName?: string;
  marketName?: string;
  subeventName?: string;
};

const buildAddOrRemoveBaseFields = (input: AddOrRemoveBetslipEventInput) => ({
  bookieCode: input.bookieCode,
  betOdds: input.betOdds,
  betName: input.betName,
  betLocation: input.betLocation,
  subeventName: input.subeventName,
  marketName: input.marketName,
  eventName: input.eventName,
});

export const buildAddToBetslipEvent = (input: AddOrRemoveBetslipEventInput): ITrackingProps => ({
  event: TRACKING_EVENTS.ADD_TO_BETSLIP,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.ADD_TO_BETSLIP],
  eventLabel: input.bookieCode,
  ...buildAddOrRemoveBaseFields(input),
});

export const buildRemoveBetEvent = (input: AddOrRemoveBetslipEventInput = {}): ITrackingProps => ({
  event: TRACKING_EVENTS.REMOVE_BET,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.REMOVE_BET],
  eventLabel: '',
  ...buildAddOrRemoveBaseFields(input),
});

export const buildBetSlipClearEvent = (): ITrackingProps => ({
  event: TRACKING_EVENTS.BET_SLIP_CLEAR,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLEAR],
  eventLabel: '',
});

export const buildBetSlipOpenEvent = (): ITrackingProps => ({
  event: TRACKING_EVENTS.BET_SLIP_OPEN,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_OPEN],
  eventLabel: '',
});

export const buildBetSlipCloseEvent = (): ITrackingProps => ({
  event: TRACKING_EVENTS.BET_SLIP_CLOSE,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLOSE],
  eventLabel: '',
});

export const buildBetslipIconClickedEvent = (): ITrackingProps => ({
  event: TRACKING_EVENTS.BETSLIP_ICON_CLICKED,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BETSLIP_ICON_CLICKED],
});

export const buildChangeBookieSelectionEvent = (): ITrackingProps => ({
  event: TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION],
});

export const buildBetSlipSignUpEvent = (bookieCode?: string): ITrackingProps => ({
  event: TRACKING_EVENTS.BET_SLIP_SIGN_UP,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_SIGN_UP],
  eventLabel: bookieCode,
});

export const buildBetOnBookmakerSiteEvent = (bookieCode?: string): ITrackingProps => ({
  event: TRACKING_EVENTS.BET_ON_BOOKMAKER_SITE,
  eventCategory: TRACKING_CATEGORIES.BETTING,
  eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_ON_BOOKMAKER_SITE],
  eventLabel: bookieCode,
});

// NOTE: custref, betSport and eventName are not currently available, so pass them as undefined in
// tracking pending investigation and resolution under CI-1167 (carried over verbatim from the
// pre-extraction implementation in betslip-context.tsx).
export type BetPlacedEventInput = {
  betAccumulator: boolean;
  betNames: string[];
  betOdds: number | string;
  betStake: number;
  bookieCode: string;
  bookieName?: string;
  count: number;
  marketNames: string[];
  subeventNames: string[];
};

export const buildBetPlacedEvent = (input: BetPlacedEventInput): ITrackingProps => {
  const genericBetTrackingData = {
    bookieName: input.bookieName,
    bookieCode: input.bookieCode,
    betStake: input.betStake,
    betOdds: input.betOdds,
    betAccumulator: input.betAccumulator,
    onsite: false,
    custref: undefined as string | undefined,
  };

  const betslipBetsBetTrackingData = {
    marketName: input.marketNames,
    betSport: undefined as (number | string)[] | undefined,
    betName: input.betNames,
    subeventName: input.subeventNames,
    eventName: undefined as (number | string)[] | undefined,
  };

  const betTrackingDataAsCSV = Object.entries({
    ...genericBetTrackingData,
    ...betslipBetsBetTrackingData,
  }).reduce(
    (acc: Record<string, unknown>, [key, val]) => {
      acc[key] = Array.isArray(val) ? val.join(',') : val;
      return acc;
    },
    {} as Record<string, unknown>,
  );

  return {
    event: TRACKING_EVENTS.BET_PLACED,
    eventCategory: TRACKING_CATEGORIES.BETTING,
    eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_PLACED],
    eventLabel: input.count === 1 ? 'Place Single Bet' : 'Place Multi Bet',
    ...betTrackingDataAsCSV,
  } as ITrackingProps;
};

/**
 * Best-effort ADD_TO_BETSLIP/REMOVE_BET signal for the OC CDN betslip widget (`UKBetslip.tsx`). The
 * CDN only echoes a bet-count change back to the host page (`ocBetslip.setCallbacks({ setBetCount })`
 * fires with no arguments — the host must re-read `getBetCount()`); it never returns bet name, odds,
 * or market. Reuses the same event names as the in-repo betslip for unified reporting, but the payload
 * is necessarily thinner — callers should not expect betName/betOdds/marketName here.
 */
export const buildCdnBetslipCountChangedEvent = (previousCount: number, currentCount: number): ITrackingProps => {
  const event = currentCount > previousCount ? TRACKING_EVENTS.ADD_TO_BETSLIP : TRACKING_EVENTS.REMOVE_BET;

  return {
    event,
    eventCategory: TRACKING_CATEGORIES.BETTING,
    eventAction: TRACKING_ACTIONS[event],
    eventLabel: '',
  };
};
